import {
  Button,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { INDICATOR, InsituDataFile } from '@/api/rest/resources/types/IndicatorProduct';
import { SuspenseBoundary } from '@/components';
import { Card } from '@/components/widgets/o/Card/Card';
import { NO_DATA_PLACEHOLDER } from '@/config/constants';
import { useGetIndicatorProductSubscriptionConfigurations } from '@/hooks/useDashboardIndicatorProductSubscriptionConfiguration';
import { Logger } from '@/lib/logs/logger';
import { useIsLimitedAccessUser } from '@/pages/shared/hooks/useIsLimitedAccessUser';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';
import { isNullish } from '@/utils/booleans';

export const DownloadableInSituFiles = () => {
  const { t } = useTranslation();

  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Card
          styles={{
            root: 'rounded-3xl p-4',
          }}
        >
          <Stack spacing={4}>
            <span className='typography-body1'>{t('navbar.insituData')}</span>
            <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
          </Stack>
        </Card>
      )}
      onError={(error, info) => {
        Logger.error(`In-situ data files table rendering error: ${error} ${info}`);
      }}
    >
      <SuspenseBoundary
        fallback={
          <Stack centerMain>
            <Spinner size={10} />
          </Stack>
        }
      >
        <DownloadableInSituFilesBase />
      </SuspenseBoundary>
    </ErrorBoundary>
  );
};

const DownloadableInSituFilesBase = () => {
  const orgIndicatorProducts = useSelectedIndicatorProducts();
  const hasInsituDataIp = orgIndicatorProducts.some((ip) => ip.name === INDICATOR.IN_SITU_PACK);
  const orgId = useOrganizationId();
  const inSituConfigurations = useGetIndicatorProductSubscriptionConfigurations(orgId, false).data?.results ?? [];
  const isLimitedAccessUser = useIsLimitedAccessUser();
  const { t } = useTranslation();

  if (!hasInsituDataIp || isLimitedAccessUser) {
    return null;
  }

  const filteredConfigurations = inSituConfigurations.filter(
    (config) => Array.isArray(config.file_info) && config.file_info.length > 0,
  );
  const files = filteredConfigurations
    .flatMap((c) => {
      return (
        c?.file_info?.map((file) => ({
          ...file,
          description: c?.description ?? '',
        })) ?? []
      );
    })
    .filter((file) => file && !isNullish(file.url)) as InsituDataFileWithDescription[];

  if (files.length === 0) {
    return null;
  }

  return (
    <>
      <Stack spacing={2}>
        <h2 className='typography-h5 text-text-primary'>{t('navbar.insituData')}</h2>
        <p className='typography-body2 text-text-secondary'>{t('reports.inSituFiles.exportDataDescription')}</p>
      </Stack>
      <InSituFilesTable files={files} />
    </>
  );
};

type InsituDataFileWithDescription = InsituDataFile & { description: string };

const InSituFilesTable = ({ files }: { files: InsituDataFileWithDescription[] }) => {
  const { t } = useTranslation();

  const handleDownload = (file: InsituDataFileWithDescription) => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const sortedFiles = [...files].sort((a, b) => new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime());

  return (
    <div className='border border-neutral-black-12 rounded-2xl overflow-hidden'>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className='whitespace-nowrap'>{t('reports.inSituFiles.table.description')}</TableHead>
            <TableHead className='whitespace-nowrap'>{t('reports.inSituFiles.table.name')}</TableHead>
            <TableHead className='whitespace-nowrap'>{t('reports.inSituFiles.table.date')}</TableHead>
            <TableHead className='whitespace-nowrap text-right'>{t('reports.inSituFiles.table.actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedFiles.map((file) => (
            <TableRow key={file.id}>
              <TableCell className='font-medium'>{file.description || NO_DATA_PLACEHOLDER}</TableCell>
              <TableCell className='text-text-secondary'>{file.visible_filename}</TableCell>
              <TableCell className='text-text-secondary'>
                {new Date(file.modified_at).toLocaleDateString('en-GB')}
              </TableCell>
              <TableCell className='flex justify-end'>
                <Button onClick={() => handleDownload(file)} aria-label={`Download ${file.visible_filename}`}>
                  {t('reports.download')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
